// Notfall-Durchsage (Erweiterung): starten (Vorlage, Text, Ziel, Dauer, Bestätigung), live verfolgen
// („angezeigt auf 7/8") und beenden. Starten/Beenden nur mit emergency.broadcast (Kundenadministrator).
import '../styles/emergency-power.css';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import {
  qkEp, TEMPLATES, useBroadcast, useBroadcasts, type Broadcast, type EmergencySound, type EmergencyTargetType, type EmergencyTemplate,
  type StartBroadcast,
} from '../api/emergency-power';
import { can, useDevices, useGroups, useLocations, useMe } from '../api/queries';
import { Icon } from '../components/Icon';
import { useToast } from '../components/toast';
import { Confirm, Dialog, Empty, Field, formatDateTime, PageHead, relativeTime, Spinner } from '../components/ui';
import { localeOf } from '../i18n';

const ICONS: Record<EmergencyTemplate, string> = { fire: '⚠', evacuation: '⚠', general: 'ℹ', closing_early: '⏰', custom: '!' };
const TEMPLATE_IDS: EmergencyTemplate[] = ['fire', 'evacuation', 'general', 'closing_early', 'custom'];
const DURATIONS = [null, 15, 30, 60, 120, 240] as const;

type Look = Pick<StartBroadcast, 'template' | 'title' | 'message' | 'background' | 'textColor' | 'blink'>;

/** Vorschau wie am Bildschirm (packages/engine/src/live/layer.ts), Blinken als Farbwechsel jede Sekunde. */
export function EmergencyPreview({ b, small }: { b: Look; small?: boolean }) {
  const [swap, setSwap] = useState(false);
  useEffect(() => {
    if (!b.blink) {
      setSwap(false);
      return;
    }
    const id = setInterval(() => setSwap((s) => !s), 1000);
    return () => clearInterval(id);
  }, [b.blink]);
  const bg = swap ? b.textColor : b.background;
  const fg = swap ? b.background : b.textColor;
  return (
    <div className="em-preview-wrap">
      <div className={`em-preview${small ? ' sm' : ''}`} style={{ background: bg, color: fg }} data-testid="em-preview">
        <div className="em-icon" aria-hidden>{ICONS[b.template]}</div>
        <div className="em-title">{b.title || '…'}</div>
        {b.message ? <div className="em-msg">{b.message}</div> : null}
      </div>
    </div>
  );
}

export function AckBar({ b }: { b: Pick<Broadcast, 'counts'> }) {
  const { t } = useTranslation();
  const { total, displayed, delivered } = b.counts;
  const pct = (n: number) => `${total ? (n / total) * 100 : 0}%`;
  return (
    <div className="ack">
      <div className="row" style={{ gap: 10, alignItems: 'baseline' }}>
        <span className="ack-big">{displayed}/{total}</span>
        <span className="muted">{t('emergency.shownOn', { shown: displayed, total })}</span>
      </div>
      <div className="ack-bar" role="progressbar" aria-valuemin={0} aria-valuemax={total} aria-valuenow={displayed} aria-label={t('emergency.shownOn', { shown: displayed, total })}>
        <span className="shown" style={{ width: pct(displayed) }} />
        <span className="got" style={{ width: pct(Math.max(0, delivered - displayed)) }} />
      </div>
    </div>
  );
}

export function EmergencyPage() {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const { data: me } = useMe();
  const { data: list, isLoading } = useBroadcasts();
  const [start, setStart] = useState(false);
  const allowed = can(me, 'emergency.broadcast');
  const active = (list ?? []).filter((b) => b.status === 'active');
  const history = (list ?? []).filter((b) => b.status !== 'active');

  return (
    <>
      <PageHead title={t('emergency.title')} sub={t('emergency.sub')}
        actions={allowed && <button className="btn danger solid big" onClick={() => setStart(true)}><Icon name="megaphone" />{t('emergency.start')}</button>} />
      {!allowed && <div className="hint" style={{ marginBottom: 16 }}>{t('emergency.noPermission')}</div>}
      {isLoading ? <Spinner /> : (
        <>
          <h2>{t('emergency.active')}</h2>
          {active.length ? (
            <div className="col" style={{ gap: 16 }}>{active.map((b) => <ActiveBroadcast key={b.id} b={b} allowed={allowed} />)}</div>
          ) : <div className="card"><Empty icon="megaphone" title={t('emergency.noneActive')} text={t('emergency.noneActiveText')} /></div>}
          <h2 style={{ marginTop: 24 }}>{t('emergency.history')}</h2>
          <div className="card scroll" style={{ padding: 10 }}>
            {history.length ? (
              <table className="list">
                <thead><tr><th>{t('emergency.headline')}</th><th>{t('emergency.colStarted')}</th><th>{t('emergency.colEnded')}</th><th>{t('emergency.colShown')}</th><th>{t('emergency.colBy')}</th></tr></thead>
                <tbody>{history.map((b) => (
                  <tr key={b.id}>
                    <td><span className="row" style={{ gap: 8 }}><span className="sw" style={{ width: 14, height: 14, borderRadius: 4, background: b.background, display: 'inline-block' }} /><b>{b.title}</b></span></td>
                    <td className="muted">{formatDateTime(b.startsAt, locale)}</td>
                    <td className="muted">{formatDateTime(b.endedAt ?? b.endsAt, locale)}{b.endReason ? ` · ${t(`emergency.endReason.${b.endReason}`)}` : ''}</td>
                    <td>{b.counts.displayed}/{b.counts.total}</td>
                    <td className="muted">{b.createdBy}</td>
                  </tr>
                ))}</tbody>
              </table>
            ) : <div className="muted">{t('emergency.noHistory')}</div>}
          </div>
        </>
      )}
      {start && <StartDialog onClose={() => setStart(false)} />}
    </>
  );
}

function ActiveBroadcast({ b, allowed }: { b: Broadcast; allowed: boolean }) {
  const { t, i18n } = useTranslation();
  const locale = localeOf(i18n.language);
  const qc = useQueryClient();
  const toast = useToast();
  const { data: detail } = useBroadcast(b.id);
  const [end, setEnd] = useState(false);
  const [busy, setBusy] = useState(false);
  const doEnd = async () => {
    setBusy(true);
    try {
      await api.post(`/emergency/broadcasts/${b.id}/end`);
      await qc.invalidateQueries({ queryKey: qkEp.broadcasts });
      toast.show(t('emergency.ended'));
      setEnd(false);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  const cur = detail ?? b;
  return (
    <div className="card em-active col" data-testid="active-broadcast">
      <div className="grid-2">
        <EmergencyPreview b={cur} small />
        <div className="col">
          <div className="row"><span className="pill error">{t('emergency.live')}</span><span className="muted small">{t(`emergency.tpl.${cur.template}.name`)}</span></div>
          <AckBar b={cur} />
          <div className="muted small">{t('emergency.startedBy', { when: relativeTime(cur.startsAt, locale), who: cur.createdBy })}</div>
          <div className="muted small">{cur.endsAt ? t('emergency.endsAt', { when: formatDateTime(cur.endsAt, locale) }) : t('emergency.untilEnded')}</div>
          {cur.counts.offline > 0 && <div className="hint warn">{t('emergency.offlineHint', { count: cur.counts.offline })}</div>}
          {allowed && <div><button className="btn danger" onClick={() => setEnd(true)}><Icon name="x" />{t('emergency.end')}</button></div>}
        </div>
      </div>
      {detail && (
        <div className="scroll">
          <table className="list">
            <thead><tr><th>{t('emergency.colScreen')}</th><th>{t('emergency.colStatus')}</th><th>{t('emergency.colTime')}</th></tr></thead>
            <tbody>{detail.devices.map((d) => (
              <tr key={d.deviceId} data-testid="ack-row">
                <td><b>{d.name}</b></td>
                <td><span className={`pill ${d.status}`}>{t(`emergency.deliv.${d.status}`)}</span></td>
                <td className="muted small">{relativeTime(d.displayedAt ?? d.deliveredAt, locale)}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {end && <Confirm title={t('emergency.endTitle')} text={t('emergency.endText')} confirmLabel={t('emergency.end')} danger busy={busy}
        onConfirm={() => void doEnd()} onClose={() => setEnd(false)} />}
    </div>
  );
}

function StartDialog({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const toast = useToast();
  const { data: devices = [] } = useDevices();
  const { data: groups = [] } = useGroups();
  const { data: locations = [] } = useLocations();
  const fill = (tpl: EmergencyTemplate) => ({
    template: tpl, ...TEMPLATES[tpl],
    title: tpl === 'custom' ? '' : t(`emergency.tpl.${tpl}.title`),
    message: tpl === 'custom' ? '' : t(`emergency.tpl.${tpl}.message`),
  });
  const [f, setF] = useState(() => fill('fire'));
  const [targetType, setTargetType] = useState<EmergencyTargetType>('tenant');
  const [ids, setIds] = useState<string[]>([]);
  const [duration, setDuration] = useState<number | null>(null);
  const [step, setStep] = useState<'edit' | 'confirm'>('edit');
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);

  const active = devices.filter((d) => d.status === 'active');
  const count = useMemo(() => {
    if (targetType === 'tenant') return active.length;
    if (targetType === 'locations') return active.filter((d) => d.locationId && ids.includes(d.locationId)).length;
    if (targetType === 'groups') return new Set(groups.filter((g) => ids.includes(g.id)).flatMap((g) => g.deviceIds).filter((id) => active.some((d) => d.id === id))).size;
    return active.filter((d) => ids.includes(d.id)).length;
  }, [targetType, ids, active, groups]);
  const options = targetType === 'locations' ? locations : targetType === 'groups' ? groups : targetType === 'devices' ? active : [];
  const valid = f.title.trim().length > 0 && count > 0;

  const send = async () => {
    setBusy(true);
    try {
      const body: StartBroadcast = { ...f, title: f.title.trim(), target: { type: targetType, ids: targetType === 'tenant' ? [] : ids }, durationMinutes: duration, confirm: true };
      await api.post('/emergency/broadcasts', body);
      await qc.invalidateQueries({ queryKey: qkEp.broadcasts });
      toast.show(t('emergency.started'));
      onClose();
    } catch (e) {
      toast.error(e);
      setBusy(false);
    }
  };

  if (step === 'confirm') {
    return (
      <Dialog title={t('emergency.confirmTitle')} onClose={onClose} actions={<>
        <button className="btn ghost" onClick={() => { setStep('edit'); setChecked(false); }}>{t('common.back')}</button>
        <button className="btn danger solid" disabled={!checked || busy} onClick={() => void send()}><Icon name="megaphone" />{t('emergency.send')}</button>
      </>}>
        <EmergencyPreview b={f} small />
        <div className="hint warn">{t('emergency.confirmText', { title: f.title.trim(), count })}</div>
        <div className="muted small">{duration ? t('emergency.durationMin', { count: duration }) : t('emergency.untilEnded')}{f.sound !== 'none' ? ` · ${t(`emergency.sounds.${f.sound}`)}` : ''}</div>
        <label className="check"><input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />{t('emergency.confirmCheck')}</label>
      </Dialog>
    );
  }

  return (
    <Dialog wide title={t('emergency.start')} onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>{t('common.cancel')}</button>
      <button className="btn danger solid" disabled={!valid} onClick={() => setStep('confirm')}>{t('common.next')}</button>
    </>}>
      <div className="field">{t('emergency.template')}
        <div className="tpl-grid" role="radiogroup" aria-label={t('emergency.template')}>
          {TEMPLATE_IDS.map((id) => (
            <button key={id} type="button" role="radio" aria-checked={f.template === id} className={`tpl${f.template === id ? ' on' : ''}`} onClick={() => setF(fill(id))}>
              <span className="sw" style={{ background: TEMPLATES[id].background }} />{t(`emergency.tpl.${id}.name`)}
            </button>
          ))}
        </div>
      </div>
      <div className="grid-2">
        <div className="col">
          <Field label={t('emergency.headline')}><input maxLength={120} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} /></Field>
          <Field label={t('emergency.message')}><textarea maxLength={1000} rows={4} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} /></Field>
          <div className="row" style={{ gap: 12 }}>
            <Field label={t('emergency.background')}><input type="color" value={f.background} onChange={(e) => setF({ ...f, background: e.target.value.toUpperCase() })} /></Field>
            <Field label={t('emergency.textColor')}><input type="color" value={f.textColor} onChange={(e) => setF({ ...f, textColor: e.target.value.toUpperCase() })} /></Field>
            <Field label={t('emergency.sound')}>
              <select value={f.sound} onChange={(e) => setF({ ...f, sound: e.target.value as EmergencySound })}>
                {(['none', 'alarm', 'chime'] as EmergencySound[]).map((s) => <option key={s} value={s}>{t(`emergency.sounds.${s}`)}</option>)}
              </select>
            </Field>
          </div>
          <label className="check"><input type="checkbox" checked={f.blink} onChange={(e) => setF({ ...f, blink: e.target.checked })} />{t('emergency.blink')}</label>
          {f.sound !== 'none' && <div className="muted small">{t('emergency.soundHint')}</div>}
        </div>
        <div className="col">
          <span className="muted small">{t('emergency.preview')}</span>
          <EmergencyPreview b={f} />
        </div>
      </div>
      <div className="grid-2">
        <div className="col">
          <Field label={t('emergency.target')}>
            <select value={targetType} onChange={(e) => { setTargetType(e.target.value as EmergencyTargetType); setIds([]); }}>
              {(['tenant', 'locations', 'groups', 'devices'] as EmergencyTargetType[]).map((x) => <option key={x} value={x}>{t(`emergency.targets.${x}`)}</option>)}
            </select>
          </Field>
          {targetType !== 'tenant' && (
            <div className="col" style={{ gap: 4, maxHeight: 160, overflow: 'auto' }}>
              {options.map((o) => (
                <label key={o.id} className="check"><input type="checkbox" checked={ids.includes(o.id)}
                  onChange={(e) => setIds(e.target.checked ? [...ids, o.id] : ids.filter((x) => x !== o.id))} />{o.name}</label>
              ))}
            </div>
          )}
          <div className={count ? 'muted small' : 'field-error'}>{t('emergency.screensCount', { count })}</div>
        </div>
        <Field label={t('emergency.duration')}>
          <select value={duration ?? ''} onChange={(e) => setDuration(e.target.value ? Number(e.target.value) : null)}>
            {DURATIONS.map((d) => <option key={d ?? 'manual'} value={d ?? ''}>{d ? t('emergency.durationMin', { count: d }) : t('emergency.untilEnded')}</option>)}
          </select>
        </Field>
      </div>
    </Dialog>
  );
}
